/* global google */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMap } from '@vis.gl/react-google-maps/dist/index.umd';
import { MarkerClusterer as GoogleMarkerClusterer } from '@googlemaps/markerclusterer/dist/index.umd';

const eventMap = {
  onClick: 'click',
  onClusteringBegin: 'clusteringbegin',
  onClusteringEnd: 'clusteringend',
  onMouseOut: 'mouseout',
  onMouseOver: 'mouseover',
};

const MarkerClusterer = ({
  children,
  options,
  onClick,
  onClusteringBegin,
  onClusteringEnd,
  onMouseOver,
  onMouseOut,
  onLoad,
  onUnmount,
  averageCenter,
  // optional convenience: let the component create markers for you
  markers: markerData,
  createMarker, // (item, map) => google.maps.Marker
}) => {
  const map = useMap();
  const [clusterer, setClusterer] = useState(null);
  const [markers, setMarkers] = useState({});
  const listenersRef = useRef([]);

  const eventHandlers = useMemo(
    () => ({
      onClick,
      onClusteringBegin,
      onClusteringEnd,
      onMouseOver,
      onMouseOut,
    }),
    [onClick, onClusteringBegin, onClusteringEnd, onMouseOver, onMouseOut]
  );

  useEffect(() => {
    if (!map) return undefined;

    const clustererInstance = new GoogleMarkerClusterer({
      map,
      ...(options || {}),
      averageCenter,
    });

    setClusterer(clustererInstance);
    if (onLoad) {
      onLoad(clustererInstance);
    }

    return () => {
      listenersRef.current.forEach((listener) => listener.remove());
      listenersRef.current = [];

      clustererInstance.clearMarkers();
      clustererInstance.setMap(null);

      if (onUnmount) {
        onUnmount(clustererInstance);
      }
      setClusterer(null);
    };
  }, [map, options, onLoad, onUnmount, averageCenter]);

  const setMarkerRef = useCallback((marker, key) => {
    setMarkers((current) => {
      const exists = current[key];
      if (marker && exists === marker) return current;
      if (!marker && !exists) return current;
      if (marker) {
        return { ...current, [key]: marker };
      }
      const { [key]: _removed, ...rest } = current;
      return rest;
    });
  }, []);

  useEffect(() => {
    if (!clusterer || !options) return;
    clusterer.setOptions(options);
  }, [clusterer, options]);

  useEffect(() => {
    if (!clusterer) return;

    listenersRef.current.forEach((listener) => listener.remove());
    listenersRef.current = [];

    Object.entries(eventMap).forEach(([propName, eventName]) => {
      const handler = eventHandlers[propName];
      if (handler) {
        listenersRef.current.push(clusterer.addListener(eventName, handler));
      }
    });
  }, [clusterer, eventHandlers]);

  useEffect(() => {
    if (!clusterer) return;
    const markerList = Object.values(markers);
    clusterer.clearMarkers();
    if (markerList.length) {
      clusterer.addMarkers(markerList);
    }
  }, [clusterer, markers]);

  useEffect(() => {
    if (!map || !markerData?.length) return undefined;

    const created = markerData.map((item, idx) => {
      const id = item.id ?? item.key ?? idx;
      const marker =
        createMarker?.(item, map) ||
        new google.maps.Marker({
          position: { lat: item.lat, lng: item.lng },
          map,
        });

      const clickListener = item.onClick
        ? marker.addListener('click', () => item.onClick(item))
        : null;

      setMarkerRef(marker, id);

      return { marker, id, clickListener };
    });

    return () => {
      created.forEach(({ marker, id, clickListener }) => {
        if (clickListener) clickListener.remove();
        marker.setMap(null);
        setMarkerRef(null, id);
      });
    };
  }, [map, markerData, createMarker, setMarkerRef]);

  if (!clusterer) return null;

  if (children) {
    return children({
      clusterer,
      setMarkerRef,
    }) || null;
  }

  return null;
};

export default MarkerClusterer;

